import React, { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
	withSalesActivityModuleProvider,
	SalesActivityProvider,
	defaultParentContext
} from "@/SalesActivity/store";
import { GlobalState } from "@/Reducers/interface";
import ZOpportunitySelect from "@organisms/ZOpportunitySelect";
import ZClientSelect from "@organisms/ZClientSelect";
import ZUserSelect from "@organisms/ZUserSelect";
import AddOpportunityDrawer from "@/Components/OpportunitiesPage/Drawers/AddOpportunityDrawer";
import AddClientDrawer from "@pages/Drawers/Clients/Client/Add";
import AddClientContactDrawer from "@pages/Drawers/Clients/ClientContact/Add";
import useDispatch from "@/Components/Utils/useDispatch";
import OpportunitiesActionCreators from "@/Actions/Opportunity";
import PipelinesActionCreators from "@/Actions/Pipelines";
import ClientsActionCreators from "@/Actions/Clients";
import {
	ClientContactBulkUpdateRequest,
	ClientsRequest
} from "@/Contracts/Clients";
import TemplatesActionCreators from "@/Actions/Templates";
import {
	TemplateListRequest,
	TemplateOwnerType
} from "@/Contracts/Templates";
import RecentActivityFeedActionCreators from "@/Actions/Others/RecentActivityFeed";
import EmailThreadActionCreators from "@/Actions/Others/EmailThread";
import EmailThreadComponent from "@/Components/Others/EmailThread";
import EmailThreadMessageDrawer from "@/Components/Others/EmailThread/Drawers/EmailThreadMessageDrawer";
import ImportEmailTemplateModal from "@/Components/Others/EmailThread/Drawers/FooterActivities/ImportEmailTemplateModal";
import TrackEmail from "@/Components/Others/EmailThread/Drawers/FooterActivities/TrackEmail";
import EmailSignatureComponent from "@/Components/Others/EmailThread/Drawers/FooterActivities/EmailSignatureComponent";
import DocumentsActionCreators from "@/Actions/Documents";
import UsersActionCreators from "@/Actions/User";
import UserSelectComponent from "@/Components/UI/UserSelect";
import EmailTemplatesActionCreators from "@/Actions/EmailTemplates";
import { CustomTemplateType } from "@/Contracts/EmailTemplates";
import { fetchMergeTagsWithNoEntityId } from "@/Services/Others/EmailThread";
import { getShippingAddressesListItems } from "@/Hooks/Client/useShippingAddressSelectionHook";
import ZTableWithDecorators from "@organisms/ZTableWithDecorators";
import { Link, useHistory } from "react-router-dom";
import { getOpportunityListPost } from "@/Services/Opportunity";
import "@/Components/Others/EmailThread/Drawers/EmailThreadMessageDrawer.less";
import SettingsActionCreators from "@/Actions/Settings";
import { ZomentumEntities } from "@/Contracts/common";
import { CustomFieldsActionCreators } from "@/Actions/CustomFields";

const SalesActivityWrapper: React.FC = ({ children }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const {
		isOpportunityLoading,
		opportunityList,
		currentActiveOpportunity,
		addOpportunityDrawerOpen,
		editOpportunityDrawerOpen,
		activeOpportunityView
	} = useSelector((state: GlobalState) => {
		return {
			isOpportunityLoading: state.opportunities.isLoading,
			opportunityList: state.opportunities.opportunityList,
			currentActiveOpportunity:
				state.opportunities.currentActiveOpportunity,
			addOpportunityDrawerOpen:
				state.opportunities.addOpportunityDrawerOpen,
			editOpportunityDrawerOpen:
				state.opportunities.editOpportunityDrawerOpen,
			activeOpportunityView: state.opportunities.activeOpportunityView
		};
	});

	const showOpportunitiesLoader = useCallback(() => {
		dispatch(OpportunitiesActionCreators.showOpportunitiesLoader());
	}, []);

	const hideOpportunitiesLoader = useCallback(() => {
		dispatch(OpportunitiesActionCreators.hideOpportunitiesLoader());
	}, []);

	const addOpportunityDrawerOpened = useCallback(() => {
		dispatch(OpportunitiesActionCreators.addOpportunityDrawerOpened());
	}, []);

	const addOpportunityDrawerClosed = useCallback(() => {
		dispatch(OpportunitiesActionCreators.addOpportunityDrawerClosed());
	}, []);

	const editOpportunityDrawerOpened = useCallback(() => {
		dispatch(OpportunitiesActionCreators.editOpportunityDrawerOpened());
	}, []);

	const editOpportunityDrawerClosed = useCallback(() => {
		dispatch(OpportunitiesActionCreators.editOpportunityDrawerClosed());
	}, []);

	const syncKanbanOpportunityDataById = useCallback(
		async (opportunityId: string) => {
			await dispatch(
				OpportunitiesActionCreators.syncKanbanOpportunityDataById(
					opportunityId
				)
			);
		},
		[]
	);

	const resetOpportunityDetails = useCallback(() => {
		dispatch(OpportunitiesActionCreators.resetOpportunityDetails());
	}, []);

	const opportunity = useMemo(
		() => ({
			isLoading: isOpportunityLoading,
			opportunityList,
			currentActiveOpportunity,
			addOpportunityDrawerOpen,
			editOpportunityDrawerOpen,
			activeOpportunityView,
			showOpportunitiesLoader,
			hideOpportunitiesLoader,
			addOpportunityDrawerOpened,
			addOpportunityDrawerClosed,
			editOpportunityDrawerOpened,
			editOpportunityDrawerClosed,
			syncKanbanOpportunityDataById,
			resetOpportunityDetails,
			ZOpportunitySelect,
			AddOpportunityDrawer,
			getOpportunityListPost
		}),
		[
			isOpportunityLoading,
			opportunityList,
			currentActiveOpportunity,
			addOpportunityDrawerOpen,
			editOpportunityDrawerOpen,
			activeOpportunityView
		]
	);

	const { pipelineList } = useSelector((state: GlobalState) => {
		return {
			pipelineList: state.pipelines.pipelineList
		};
	});
	const getAllPipelines = useCallback(async () => {
		return await dispatch(PipelinesActionCreators.getAllPipelines());
	}, []);
	const pipeline = useMemo(
		() => ({
			pipelineList,
			getAllPipelines
		}),
		[pipelineList]
	);

	const {
		isClientsLoading,
		clientList,
		clientContactList,
		addClientDrawerOpen,
		editClientDrawerOpen,
		currentActiveClient,
		addClientContactDrawerOpen
	} = useSelector((state: GlobalState) => {
		return {
			isClientsLoading: state.clients.isLoading,
			clientList: state.clients.clientList,
			clientContactList: state.clients.clientContactList,
			addClientDrawerOpen: state.clients.addClientDrawerOpen,
			editClientDrawerOpen: state.clients.editClientDrawerOpen,
			currentActiveClient: state.clients.currentActiveClient,
			addClientContactDrawerOpen: state.clients.addClientContactDrawerOpen
		};
	});
	const showClientsLoader = useCallback(() => {
		dispatch(ClientsActionCreators.showClientsLoader());
	}, []);

	const hideClientsLoader = useCallback(() => {
		dispatch(ClientsActionCreators.hideClientsLoader());
	}, []);

	const getClients = useCallback(async (clientsRequest: ClientsRequest) => {
		return await dispatch(ClientsActionCreators.getClients(clientsRequest));
	}, []);

	const resetClients = useCallback(() => {
		dispatch(ClientsActionCreators.resetClients());
	}, []);

	const resetClientContacts = useCallback(() => {
		dispatch(ClientsActionCreators.resetClientContacts());
	}, []);

	const resetClientDetails = useCallback(() => {
		dispatch(ClientsActionCreators.resetClientDetails());
	}, []);

	const addClientDrawerOpened = useCallback(() => {
		dispatch(ClientsActionCreators.addClientDrawerOpened());
	}, []);

	const editClientDrawerOpened = useCallback(() => {
		dispatch(ClientsActionCreators.editClientDrawerOpened());
	}, []);

	const addClientDrawerClosed = useCallback(() => {
		dispatch(ClientsActionCreators.addClientDrawerClosed());
	}, []);

	const editClientDrawerClosed = useCallback(() => {
		dispatch(ClientsActionCreators.editClientDrawerClosed());
	}, []);

	const addClientContactDrawerOpened = useCallback(() => {
		dispatch(ClientsActionCreators.addClientContactDrawerOpened());
	}, []);

	const addClientContactDrawerClosed = useCallback(() => {
		dispatch(ClientsActionCreators.addClientContactDrawerClosed());
	}, []);

	const bulkUpdateClientContactEmail = useCallback(
		async (bulkUpdateRequest: ClientContactBulkUpdateRequest) => {
			return await dispatch(
				ClientsActionCreators.bulkUpdateClientContactEmail(
					bulkUpdateRequest
				)
			);
		},
		[]
	);

	const client = useMemo(
		() => ({
			...defaultParentContext.client,
			isLoading: isClientsLoading,
			clientList,
			clientContactList,
			addClientDrawerOpen,
			editClientDrawerOpen,
			currentActiveClient,
			showClientsLoader,
			hideClientsLoader,
			getClients,
			resetClients,
			resetClientContacts,
			resetClientDetails,
			addClientDrawerOpened,
			editClientDrawerOpened,
			addClientDrawerClosed,
			editClientDrawerClosed,
			addClientContactDrawerOpened,
			addClientContactDrawerClosed,
			addClientContactDrawerOpen,
			bulkUpdateClientContactEmail,
			ZClientSelect,
			AddClientDrawer,
			AddClientContactDrawer
		}),
		[
			isClientsLoading,
			clientList,
			clientContactList,
			addClientDrawerOpen,
			addClientContactDrawerOpen,
			editClientDrawerOpen,
			currentActiveClient
		]
	);

	const { isTemplatesLoading, templateList, templatesCount } = useSelector(
		(state: GlobalState) => {
			return {
				isTemplatesLoading: state.templates.isLoading,
				templateList: state.templates.templateList,
				templatesCount: state.templates.templatesCount
			};
		}
	);
	const getTemplates = useCallback(
		async (
			templateListRequest: TemplateListRequest,
			templateOwnerType: TemplateOwnerType
		) => {
			return await dispatch(
				TemplatesActionCreators.getTemplates(
					templateListRequest,
					templateOwnerType
				)
			);
		},
		[]
	);
	const showTemplatesLoader = useCallback(() => {
		dispatch(TemplatesActionCreators.showTemplatesLoader());
	}, []);

	const hideTemplatesLoader = useCallback(() => {
		dispatch(TemplatesActionCreators.hideTemplatesLoader());
	}, []);

	const template = useMemo(
		() => ({
			isLoading: isTemplatesLoading,
			templateList,
			templatesCount,
			getTemplates,
			showTemplatesLoader,
			hideTemplatesLoader
		}),
		[isTemplatesLoading, templateList, templatesCount]
	);

	const { isRecentActivityFeedLoading, emailThreadDrawerOpen } = useSelector(
		(state: GlobalState) => {
			return {
				isRecentActivityFeedLoading: state.recentActivityFeed.isLoading,
				emailThreadDrawerOpen:
					state.recentActivityFeed.emailThreadDrawerOpen
			};
		}
	);
	const emailThreadDrawerOpened = useCallback(() => {
		dispatch(RecentActivityFeedActionCreators.emailThreadDrawerOpened());
	}, []);
	const emailThreadDrawerClosed = useCallback(() => {
		dispatch(RecentActivityFeedActionCreators.emailThreadDrawerClosed());
	}, []);

	const recentActivityFeed = useMemo(
		() => ({
			isLoading: isRecentActivityFeedLoading,
			emailThreadDrawerOpen,
			emailThreadDrawerOpened,
			emailThreadDrawerClosed
		}),
		[isRecentActivityFeedLoading, emailThreadDrawerOpen]
	);

	const {
		isEmailThreadLoading,
		emailThreadMessageDrawerOpen,
		missingEmailModalOpen,
		emailMergeTagsSystemVariable
	} = useSelector((state: GlobalState) => {
		return {
			isEmailThreadLoading: state.emailThread.isLoading,
			emailThreadMessageDrawerOpen:
				state.emailThread.emailThreadMessageDrawerOpen,
			missingEmailModalOpen: state.emailThread.missingEmailModalOpen,
			emailMergeTagsSystemVariable:
				state.emailThread.emailMergeTagsSystemVariable
		};
	});
	const emailThreadMessageDrawerOpened = useCallback(
		(sendEmailWithoutDocument?: boolean) => {
			dispatch(
				EmailThreadActionCreators.emailThreadMessageDrawerOpened(
					sendEmailWithoutDocument
				)
			);
		},
		[]
	);
	const emailThreadMessageDrawerClosed = useCallback(() => {
		dispatch(EmailThreadActionCreators.emailThreadMessageDrawerClosed());
	}, []);

	const missingEmailModalOpened = useCallback(() => {
		dispatch(EmailThreadActionCreators.missingEmailModalOpened());
	}, []);

	const missingEmailModalClosed = useCallback(() => {
		dispatch(EmailThreadActionCreators.missingEmailModalClosed());
	}, []);

	const resetMergeTags = useCallback(() => {
		dispatch(EmailThreadActionCreators.resetMergeTags());
	}, []);

	const fetchMergeTagsWithoutId = useCallback(async (entityType: string) => {
		await dispatch(
			EmailThreadActionCreators.fetchMergeTagsWithoutId(entityType)
		);
	}, []);

	const emailThread = useMemo(
		() => ({
			isLoading: isEmailThreadLoading,
			emailThreadMessageDrawerOpen,
			missingEmailModalOpen,
			emailMergeTagsSystemVariable,
			emailThreadMessageDrawerOpened,
			emailThreadMessageDrawerClosed,
			missingEmailModalOpened,
			missingEmailModalClosed,
			resetMergeTags,
			fetchMergeTagsWithoutId,
			EmailThreadComponent,
			EmailThreadMessageDrawer,
			ImportEmailTemplateModal,
			TrackEmail,
			EmailSignatureComponent
		}),
		[
			isEmailThreadLoading,
			emailThreadMessageDrawerOpen,
			missingEmailModalOpen,
			emailMergeTagsSystemVariable
		]
	);

	const {
		addDocumentDrawerOpen,
		editOrCloneDocumentDrawerOpen,
		sendDocumentDrawerOpen
	} = useSelector((state: GlobalState) => {
		return {
			addDocumentDrawerOpen: state.documents.addDocumentDrawerOpen,
			editOrCloneDocumentDrawerOpen:
				state.documents.editOrCloneDocumentDrawerOpen,
			sendDocumentDrawerOpen: state.documents.sendDocumentDrawerOpen
		};
	});
	const addDocumentDrawerOpened = useCallback(() => {
		dispatch(DocumentsActionCreators.addDocumentDrawerOpened());
	}, []);

	const editOrCloneDocumentDrawerOpened = useCallback(() => {
		dispatch(DocumentsActionCreators.editOrCloneDocumentDrawerOpened());
	}, []);

	const sendDocumentDrawerOpened = useCallback(() => {
		dispatch(DocumentsActionCreators.sendDocumentDrawerOpened());
	}, []);

	const addDocumentDrawerClosed = useCallback(() => {
		dispatch(DocumentsActionCreators.addDocumentDrawerClosed());
	}, []);

	const editOrCloneDocumentDrawerClosed = useCallback(() => {
		dispatch(DocumentsActionCreators.editOrCloneDocumentDrawerClosed());
	}, []);

	const sendDocumentDrawerClosed = useCallback(() => {
		dispatch(DocumentsActionCreators.sendDocumentDrawerClosed());
	}, []);

	const document = useMemo(
		() => ({
			addDocumentDrawerOpen,
			editOrCloneDocumentDrawerOpen,
			sendDocumentDrawerOpen,
			addDocumentDrawerOpened,
			editOrCloneDocumentDrawerOpened,
			sendDocumentDrawerOpened,
			addDocumentDrawerClosed,
			editOrCloneDocumentDrawerClosed,
			sendDocumentDrawerClosed
		}),
		[
			addDocumentDrawerOpen,
			editOrCloneDocumentDrawerOpen,
			sendDocumentDrawerOpen
		]
	);

	const {
		isUserLoading,
		userData,
		subscriptions,
		taxRegionsList,
		configurationSettings,
		billingInfo,
		userCompany,
		companyUserList,
		emailSignatures
	} = useSelector((state: GlobalState) => {
		return {
			isUserLoading: state.user.isLoading,
			userData: state.user.userData,
			subscriptions: state.user.subscriptions,
			taxRegionsList: state.user.taxRegionsList,
			configurationSettings: state.user.configurationSettings,
			billingInfo: state.user.billingInfo,
			userCompany: state.user.userCompany,
			companyUserList: state.user.companyUserList,
			emailSignatures: state.user.emailSignatures
		};
	});
	const getUserCompany = useCallback(async () => {
		await dispatch(UsersActionCreators.getUserCompany());
	}, []);

	const user = useMemo(
		() => ({
			isLoading: isUserLoading,
			userData,
			subscriptions,
			taxRegionsList,
			configurationSettings,
			billingInfo,
			userCompany,
			companyUserList,
			emailSignatures,
			getUserCompany,
			ZUserSelect,
			UserSelectComponent
		}),
		[
			isUserLoading,
			userData,
			subscriptions,
			taxRegionsList,
			configurationSettings,
			billingInfo,
			userCompany,
			companyUserList,
			emailSignatures
		]
	);

	const fetchTemplateByType = useCallback(
		async (customTemplateType: CustomTemplateType) => {
			return await dispatch(
				EmailTemplatesActionCreators.fetchTemplateByType(
					customTemplateType
				)
			);
		},
		[]
	);

	const emailTemplate = useMemo(
		() => ({
			fetchTemplateByType
		}),
		[]
	);

	const {
		isSettingsLoading,
		appUpdateBannerVisible,
		customFieldList,
		isFormEdited
	} = useSelector((state: GlobalState) => {
		return {
			isSettingsLoading: state.settings.isLoading,
			appUpdateBannerVisible: state.settings.appUpdateBannerVisible,
			customFieldList: state.customFields.customFieldList.log_call,
			isFormEdited: state.settings.isFormEdited
		};
	});

	const getCustomFields = useCallback(
		async (entityType: ZomentumEntities) => {
			await dispatch(
				CustomFieldsActionCreators.getCustomFields(entityType)
			);
		},
		[]
	);

	const setFormEdited = useCallback((formKey: string) => {
		dispatch(SettingsActionCreators.setFormEdited(formKey));
	}, []);

	const resetFormEdited = useCallback((formKey: string) => {
		dispatch(SettingsActionCreators.resetFormEdited(formKey));
	}, []);

	const setting = useMemo(
		() => ({
			isLoading: isSettingsLoading,
			appUpdateBannerVisible,
			customFieldList,
			getCustomFields,
			isFormEdited,
			setFormEdited,
			resetFormEdited
		}),
		[
			isSettingsLoading,
			appUpdateBannerVisible,
			customFieldList,
			isFormEdited
		]
	);

	const { isBannerOpen, vendorPromotionBannerDismissed } = useSelector(
		(state: GlobalState) => {
			return {
				isBannerOpen:
					state.bannerNotification.isOpen ||
					state.bannerNotification.isPaymentDueBannerOpen,
				vendorPromotionBannerDismissed:
					state.bannerNotification.vendorPromotionBannerDismissed
			};
		}
	);
	const historyPush = useCallback(
		(location: string) => history.push(location),
		[]
	);
	const misc = useMemo(
		() => ({
			isBannerOpen,
			vendorPromotionBannerDismissed,
			fetchMergeTagsWithNoEntityId,
			getShippingAddressesListItems,
			ZTableWithDecorators,
			Link,
			historyPush
		}),
		[isBannerOpen, vendorPromotionBannerDismissed]
	);

	return (
		<SalesActivityProvider
			{...defaultParentContext}
			opportunity={opportunity}
			pipeline={pipeline}
			client={client}
			template={template}
			recentActivityFeed={recentActivityFeed}
			emailThread={emailThread}
			document={document}
			user={user}
			emailTemplate={emailTemplate}
			setting={setting}
			misc={misc}
			integrations={configurationSettings?.company_configs?.integrations}
		>
			{children}
		</SalesActivityProvider>
	);
};

export default withSalesActivityModuleProvider(memo(SalesActivityWrapper));
